import { Component, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { ContractsForPartyMultiSelectComponent } from '../contracts-for-party-multi-select/contracts-for-party-multi-select.component';
import { ContractInfo } from '../../../objects/timecard-spec';

/**
 * Step 2 of the timecard activation flow — appears after the existing
 * "Enable Timecards" confirm dialog. Forces the admin to pick ≥1 contract
 * before the timecard spec is created.
 *
 * Returns `ContractInfo[]` on close when the admin confirms; returns `null`
 * (the default close result) when they cancel. The caller (vendor.component)
 * aborts the whole activation if null is returned.
 */
export interface ContractSelectionDialogData {
  partyDisplayName: string;
  partyNum: string;
  parentVendorNum?: string | null;
}

@Component({
  selector: 'app-contract-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ContractsForPartyMultiSelectComponent
  ],
  template: `
    <h2 mat-dialog-title>Select Contracts</h2>
    <mat-dialog-content>
      <p class="lead">
        Choose the contracts <strong>{{ data.partyDisplayName }}</strong> works
        under. The HCP can only submit timecards against contracts you select
        here. At least one is required.
      </p>
      <app-contracts-for-party-multi-select
        [partyNum]="data.partyNum"
        [parentVendorNum]="data.parentVendorNum"
        [(ngModel)]="selected">
      </app-contracts-for-party-multi-select>
      @if (showValidationError()) {
        <p class="error-message">
          <mat-icon>error_outline</mat-icon>
          At least one contract must be selected.
        </p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="cancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="confirm()">
        Continue
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .lead { margin: 0 0 12px; color: #444; }
    .error-message {
      display: flex; align-items: center; gap: 6px;
      margin-top: 12px; color: #c62828; font-size: 13px;
    }
    .error-message mat-icon {
      font-size: 18px; width: 18px; height: 18px;
    }
    mat-dialog-content { min-width: 480px; max-width: 640px; }
  `]
})
export class ContractSelectionDialogComponent {
  selected: ContractInfo[] = [];
  showValidationError = signal(false);

  constructor(
    public dialogRef: MatDialogRef<ContractSelectionDialogComponent, ContractInfo[] | null>,
    @Inject(MAT_DIALOG_DATA) public data: ContractSelectionDialogData
  ) {}

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!Array.isArray(this.selected) || this.selected.length === 0) {
      this.showValidationError.set(true);
      return;
    }
    this.dialogRef.close(this.selected);
  }
}
